import { z } from "zod";
import { laneKindSchema } from "../../shared/schemas/boards";
import { idSchema, issuePrioritySchema, issueTypeKeySchema, type IssuePriority } from "../../shared/schemas/issues";

/**
 * What the issue queries take: `assignee` is a person's id, `unassigned` or `me`; `status` is a status's id; `blocked`
 * and `recent` are the board's quick filters, Blocked and Recently updated (BUILD_SPEC §12.2).
 */
export type IssueFilters = {
  status?: string;
  assignee?: string;
  type?: string;
  priority?: IssuePriority;
  q?: string;
  blocked?: "true";
  recent?: "true";
  /** A saved filter's id (BUILD_SPEC §12.9), as one of the quick filters. */
  filter?: string;
};

/** URL search params for the board/list routes. Resilient: bad values are dropped, not thrown.
 *  `issue` is the open-drawer key — carried here but not a filter. */
export const issueSearchSchema = z.object({
  status: idSchema.optional().catch(undefined),
  assignee: z.string().min(1).max(64).optional().catch(undefined),
  type: issueTypeKeySchema.optional().catch(undefined),
  priority: issuePrioritySchema.optional().catch(undefined),
  q: z.string().min(1).max(200).optional().catch(undefined),
  blocked: z.boolean().optional().catch(undefined),
  recent: z.boolean().optional().catch(undefined),
  /** A saved filter, chosen as one of the quick filters. */
  filter: idSchema.optional().catch(undefined),
  /** A board's swimlanes, when the person chose other than its default. */
  lane: laneKindSchema.optional().catch(undefined),
  issue: z.string().min(1).optional().catch(undefined),
});

export type IssueSearch = z.infer<typeof issueSearchSchema>;

/** Map validated URL search → filter object; drops `issue`. `me` stays `me`: the server knows who that is. */
export function searchToFilters(search: IssueSearch): IssueFilters {
  return {
    status: search.status,
    assignee: search.assignee,
    type: search.type,
    priority: search.priority,
    q: search.q,
    blocked: search.blocked === true ? "true" : undefined,
    recent: search.recent === true ? "true" : undefined,
    filter: search.filter,
  };
}

/** True when any real filter (not the drawer `issue` param) is set. */
/** The same address with none of its filters: what "Clear filters" goes to (BUILD_SPEC §4.6). */
export function withoutFilters<T extends IssueSearch>(search: T): T {
  return {
    ...search,
    status: undefined,
    assignee: undefined,
    type: undefined,
    priority: undefined,
    q: undefined,
    blocked: undefined,
    recent: undefined,
    filter: undefined,
  };
}

export function hasActiveFilters(search: IssueSearch): boolean {
  return Boolean(
    search.status ||
    search.assignee ||
    search.type ||
    search.priority ||
    search.q ||
    search.blocked ||
    search.recent ||
    search.filter,
  );
}
